import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from '../general-components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import styles from './ApprovedComponent.module.css';
import {
    approveImplant,
    editApprovalComment,
    getApprovalInfo,
    removeImplantApproval,
} from '../../services/apiService';
import EditLog from '../protocols/EditLog/EditLog';
import ConfirmationModal from '../general/modal/ConfirmationModal';
import AuthContext from '../../store/auth-context';
import GppGoodIcon from '@mui/icons-material/GppGood';
import PERMISSION_KEYS from '../../constants/permission-keys';

const ApprovedComponent: React.FC<{ implantId: string }> = ({ implantId }) => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const context = ctx.getContext();

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [info, setInfo] = useState<any>();

    const [comment, setComment] = useState('');
    const [isRemoving, setIsRemoving] = useState(false);

    const confirmApproval = async () => {
        await approveImplant(implantId, comment);
        const name = (await ctx.getAllInfo()).user.name;
        setInfo({
            ...info,
            status: 'Approved',
            comment,
            approvedBy: name,
            approvedAt: new Date().toDateString(),
        });
        togglePreview();
    };

    const removeApproval = async () => {
        setIsRemoving(true);
        await removeImplantApproval(implantId);
        setInfo({});
        setIsRemoving(false);
    };

    const editComment = async () => {
        await editApprovalComment(implantId, comment);
        setInfo({ ...info, comment });
        togglePreview();
    };

    const togglePreview = () => {
        setShowConfirmation(!showConfirmation);
    };

    const getData = async () => {
        const i = await getApprovalInfo(implantId);
        setInfo(i);
        setComment(comment);
    };

    useEffect(() => {
        getData();
    }, []);

    return (
        <>
            {showConfirmation && (
                <ConfirmationModal
                    title={t('approvedComponent.modal.title')}
                    message={t('approvedComponent.modal.message')}
                    onClose={togglePreview}
                    confirmText={
                        info?.status === 'Approved'
                            ? t('approvedComponent.modal.saveEdit')
                            : t('approvedComponent.modal.approve')
                    }
                    cancelText={t('approvedComponent.modal.cancel')}
                    onConfirm={
                        info?.status === 'Approved'
                            ? editComment
                            : confirmApproval
                    }
                >
                    <textarea
                        className={styles.comment}
                        defaultValue={info.comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </ConfirmationModal>
            )}
            {ctx.hasPermission(PERMISSION_KEYS.approve) &&
                info?.status !== 'Approved' && (
                    <Button type={ButtonType.dept} onClick={togglePreview}>
                        <FontAwesomeIcon icon={faThumbsUp} />
                        {t('approvedComponent.approveButton')}
                    </Button>
                )}
            {info?.status === 'Approved' && (
                <div>
                    <EditLog
                        title={
                            <>
                                <GppGoodIcon />{' '}
                                {t('approvedComponent.approvedStatus.title', {
                                    departmentName: context.department.name,
                                })}
                            </>
                        }
                        approve
                        log={[
                            {
                                name: info.approvedBy,
                                comment: info.comment,
                                date: new Date(info.approvedAt),
                            },
                        ]}
                    >
                        <div className={'flex flex-row'}>
                            {ctx.hasPermission(PERMISSION_KEYS.approve) && (
                                <Button
                                    className={styles.remove}
                                    small={true}
                                    type={ButtonType.tietary}
                                    onClick={togglePreview}
                                >
                                    {t(
                                        'approvedComponent.approvedStatus.editComment'
                                    )}
                                </Button>
                            )}
                            {ctx.hasPermission(PERMISSION_KEYS.approve) && (
                                <Button
                                    loading={isRemoving}
                                    className={styles.remove}
                                    small={true}
                                    type={ButtonType.cancel}
                                    onClick={removeApproval}
                                >
                                    {t(
                                        'approvedComponent.approvedStatus.removeApproval'
                                    )}
                                </Button>
                            )}
                        </div>
                    </EditLog>
                </div>
            )}
        </>
    );
};

export default ApprovedComponent;
